/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function CommonFormats() {
  return (
    <>
      <Meta
        title="Using Common Formats and Parameters"
        description="Learn how to work with representations, headers, parameters, and batch requests in the Vimeo API."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Current>Using Common Formats and Parameters</Path.Current>
          </Path>
        }
        label="Using Common Formats and Parameters"
        nav={{ page: 'common-formats' }}
      >
        <Header>Using Common Formats and Parameters</Header>

        <Paragraph size="2">
          As you work with the Vimeo API, some common themes begin to emerge, with some common
          strategies for optimizing your success. We've collected these here for your easy
          reference.
        </Paragraph>

        <Paragraph size="2">
          This section of the developer site discusses representations, headers, parameters, and
          batch requests.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> This information is current for our latest API release, version
          3.4. For complete details on 3.4, see our{' '}
          <Link variant="minimal" href="/api/changelog">
            Changelog
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="working-with-representations">
          Working with representations
        </Header>
        <Paragraph size="2">
          When you send a request to the Vimeo API, we return the requested data as a{' '}
          <em>representation</em>, or a hierarchical JSON object with multiple fields and values
          that describe a particular Vimeo resource: a video, a channel, a member account, and so
          on. We try to include only the data that you need based on the resource that you request.
          However, depending on the type of resource and the endpoint that you're requesting it
          from, the representation can be very extensive, with complete information about a video,
          for example, and the account details of the Vimeo member who owns it.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> Make sure to encode your data as UTF-8 to avoid errors. We expect
          all data to be UTF-8-encoded, and we respond with UTF-8-encoded data also.
        </StyledNotice>

        <Paragraph size="2">
          To consider a more straightforward example, if you request a specific thumbnail image for
          a video from the <code>{`/videos/{video_id}/pictures/{picture_id}`}</code> endpoint, the
          representation of the resource that the API sends back looks something like this:
        </Paragraph>

        <CodeBlock language="json">
          {{
            uri: '/videos/174560759/pictures/581495283',
            active: true,
            type: 'custom',
            sizes: [
              {
                width: 1920,
                height: 1440,
                link: 'https://i.vimeocdn.com/video/581495283_1280x720.jpg?r=pad',
                link_with_play_button:
                  'https://i.vimeocdn.com/filter/overlay?src0=https%3A%2F%2Fi.vimeocdn.com%2Fvideo%2F581495283_1280x720.jpg&src1=http%3A%2F%2Ff.vimeocdn.com%2Fp%2Fimages%2Fcrawler_play.png',
              },
            ],
            resource_key: '5f6fa37c48689d8afcd2d66f4d01473415338160',
          }}
        </CodeBlock>

        <Paragraph size="2">
          The fields and their values describe the thumbnail in question. You get the URI of the
          resource, the URL for accessing the actual image file, the width and height of the image,
          and so on.
        </Paragraph>

        <Paragraph size="2">
          Notice that this endpoint returns a single resource only. But what if you want to see all
          the thumbnails that belong to a particular video? You'd use the more general{' '}
          <code>{`/videos/{video_id}/pictures`}</code> endpoint, and you'd get back not just one
          representation but an array of them — a <em>collection representation</em> — along with
          additional fields that describe the set. Depending on the number of resources in the
          collection, we might break up the response into multiple pages; many of the collection
          fields are for navigating the pagination scheme.
        </Paragraph>

        <Paragraph size="2">
          The collection of thumbnails looks something like this, minus the individual resource
          representations, which appear as an array in the{' '}
          <code>
            <b>data</b>
          </code>{' '}
          field:
        </Paragraph>

        <CodeBlock language="json">
          {{
            total: 12,
            page: 2,
            per_page: 10,
            paging: {
              next: null,
              previous: '/videos/174560759/pictures?page=1',
              first: '/videos/174560759/pictures?page=1',
              last: '/videos/174560759/pictures?page=2',
            },
            data: [],
          }}
        </CodeBlock>

        <Paragraph size="2">
          See{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          for an explanation of these fields.
        </Paragraph>

        <GuideTable
          tableNumber={1}
          description="Collection representation fields"
          headers={['Field', 'Data type', 'Description']}
          rows={[
            [
              <code>
                <b>total</b>
              </code>,
              'Number',
              'The total number of resources in the collection.',
            ],
            [
              <code>
                <b>page</b>
              </code>,
              'Number',
              'The current page of the response.',
            ],
            [
              <code>
                <b>per_page</b>
              </code>,
              'Number',
              'The number of resources to include on a single page of the response, up to a maximum of 100. The default is 25.',
            ],
            [
              <code>
                <b>paging</b>
              </code>,
              'Object',
              'An object containing the pagination URIs.',
            ],
            [
              <code>
                <b>paging.next</b>
              </code>,
              'String',
              'The URI for returning the next page of the response.',
            ],
            [
              <code>
                <b>paging.previous</b>
              </code>,
              'String',
              'The URI for returning the previous page of the response.',
            ],
            [
              <code>
                <b>paging.first</b>
              </code>,
              'String',
              'The URI for returning the first page of the response.',
            ],
            [
              <code>
                <b>paging.last</b>
              </code>,
              'String',
              'The URI for returning the last page of the response.',
            ],
            [
              <code>
                <b>data</b>
              </code>,
              'Array',
              'An array of the resource representations included in the collection. Each object in the array represents a single resource.',
            ],
          ]}
        />

        <Paragraph size="2">
          To step through a multi-page response, send a GET request to the URI in any of the
          pagination fields:{' '}
          <b>
            <code>paging.next</code>
          </b>
          ,{' '}
          <b>
            <code>paging.previous</code>
          </b>
          , and so on. You can also specify your own value of page to return that particular{' '}
          <b>
            <code>page</code>
          </b>{' '}
          of results; see{' '}
          <Link variant="minimal" href="#using-the-pagination-parameter">
            Using the page and per_page parameters
          </Link>{' '}
          below for details.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> The value of a pagination field can be <code>null</code> when there
          is no corresponding page. For example, on the first page of the response, there is no
          previous page to go to, so{' '}
          <b>
            <code>paging.previous</code>
          </b>{' '}
          gets the null value. But on the last page of the response,{' '}
          <b>
            <code>paging.next</code>
          </b>{' '}
          gets the null value.
        </StyledNotice>

        <Header size="2" id="working-with-headers">
          Working with headers
        </Header>
        <Paragraph size="2">
          The Vimeo API uses HTTP headers to pass important pieces of information about your
          request, including the version number of the API that you want to use and the
          authorization of your app to access private data.
        </Paragraph>

        <Paragraph size="2">In this section, we discuss the following headers:</Paragraph>

        <ul>
          <li>
            <Link variant="minimal" href="#using-the-accept-header">
              <code>
                <b>Accept</b>
              </code>
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#using-the-authorization-header">
              <code>
                <b>Authorization</b>
              </code>
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#using-the-method-override-header">
              <code>
                <b>X-HTTP-Method-Override</b>
              </code>
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#using-the-if-modified-header">
              <code>
                <b>If-Modified-Since</b>
              </code>
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#using-the-user-agent-header">
              <code>
                <b>User-Agent</b>
              </code>
            </Link>
          </li>
        </ul>

        <Header size="3" id="using-the-accept-header">
          Using the{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          header
        </Header>
        <Paragraph size="2">
          We're constantly updating and improving the API, and while we try to ensure backwards
          compatibility, there's always a chance that we'll introduce a change that affects the way
          your app works.
        </Paragraph>

        <Paragraph size="2">
          To get around any problems that this might cause, we recommend that you include the{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          header with every API request that you make. This header enables you to target your
          request to a particular version of the API. It looks like this in HTTP:
        </Paragraph>

        <CodeBlock headers={['Accept: application/vnd.vimeo.*+json;version={version_number}']} />

        <Paragraph size="2">
          Normally, you set the value of the placeholder to the current version of the API, which is
          3.4. But if you're troubleshooting your app, and you know that an older version of the API
          works perfectly, say version 3.0, you'd substitute <code>3.0</code> for the placeholder
          value. The API then handles the request as if it were for version 3.0, and your app goes
          back to working properly.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> For details on each API version going back to 3.0, see our{' '}
          <Link variant="minimal" href="/api/changelog">
            Changelog
          </Link>
          .
        </StyledNotice>

        <Paragraph size="2">
          The{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          header that we gave above is fine for any use that you might have for it. But you can also
          tweak the syntax for specific cases.
        </Paragraph>

        <Paragraph size="2">
          The first part of the header, <code>application/vnd.vimeo.*</code>, indicates the type of
          resource that you expect to get back from the endpoint. The <code>*</code> in this context
          is a wildcard character for any supported Vimeo resource. But if you want to name a
          specific type, you can replace the wildcard with any of the values in{' '}
          <Link variant="minimal" href="#table-2">
            Table 2
          </Link>
          . Just make sure that this value corresponds exactly with the endpoint that you're hitting
          and what the endpoint responds with, or the API will return an error.
        </Paragraph>

        <GuideTable
          tableNumber={2}
          description={
            <span>
              Resource types for the{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              header
            </span>
          }
          headers={['Value', 'Endpoint']}
          rows={[
            [<code>album</code>, <code>/albums</code>],
            [<code>category</code>, <code>/categories</code>],
            [<code>channel</code>, <code>/channels</code>],
            [<code>comment</code>, <code>{`/videos/{video_id}/comments`}</code>],
            [<code>credit</code>, <code>{`/videos/{video_id}/credits`}</code>],
            [<code>group</code>, <code>/groups</code>],
            [
              <code>portfolio</code>,
              <div>
                <code>{`/users/{user_id}/portfolios`}</code>
                <br />
                <code>/me/portfolios</code>
              </div>,
            ],
            [<code>uploadattempt</code>, <code>{`/users/{user_id}/uploads/{upload}`}</code>],
            [
              <code>user</code>,
              <div>
                <code>/users</code>
                <br />
                <code>/me</code>
              </div>,
            ],
            [<code>video</code>, <code>/videos</code>],
          ]}
        />

        <Paragraph size="2">
          You can also (usually) request different versions of different resources. (We say usually
          because this feature is still experimental.) For example, to return the 3.0 user
          representation but the 3.4 video representation, use the following{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          header:
        </Paragraph>

        <CodeBlock
          headers={[
            'Accept: application/vnd.vimeo.user+json;version=3.0,application/vnd.vimeo.video+json;version=3.4',
          ]}
        />

        <Paragraph size="2">
          If you're curious, this is an implementation of the{' '}
          <code>
            <b>q</b>
          </code>{' '}
          parameter from the{' '}
          <Link
            variant="minimal"
            href="https://www.w3.org/Protocols/rfc2616/rfc2616-sec14.html#sec14.1"
          >
            W3C Accept header spec
          </Link>
          .
        </Paragraph>

        <Header size="3" id="using-the-authorization-header">
          Using the{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          header
        </Header>
        <Paragraph size="2">
          The{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          header enables you to provide the necessary credentials for accessing private Vimeo data.
        </Paragraph>

        <Paragraph size="2">
          Vimeo uses the OAuth 2.0 authentication protocol, so, most commonly, the{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          header contains the bearer access token, which is a string of characters that identifies
          your app and your user:
        </Paragraph>

        <CodeBlock headers={['Authorization: bearer {access_token}']} />

        <Paragraph size="2">
          If we recognize the token, and if its scopes match the kind of data that you're requesting
          or the kind of operation that you're attempting, the API completes your request.
        </Paragraph>

        <Paragraph size="2">
          For cases where you don't yet have an access token and you're using the API to generate
          one (according to a specific OAuth 2.0 workflow), the{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          header provides basic authentication, where you send the Base64-encoded string of your
          app's client identifier and client secret, like this:
        </Paragraph>

        <CodeBlock headers={['Authorization: basic {base64_string}']} />

        <Paragraph size="2">Here's the procedure for generating the string:</Paragraph>

        <ol>
          <li>
            Find your app's client identifier and client secret. These values appear on your app's
            information page, which you can access from{' '}
            <Link variant="minimal" href="/apps">
              My Apps
            </Link>
            .
          </li>
          <li>
            Create a string starting with the client identifier, followed by a colon (<code>:</code>
            ), followed by the client secret.
          </li>
          <li>Encode this string in Base64.</li>
        </ol>

        <Paragraph size="2">
          For a complete discussion about OAuth 2.0 on Vimeo, see{' '}
          <Link variant="minimal" href="/api/authentication">
            Working with Authentication
          </Link>
          .
        </Paragraph>

        <Header size="3" id="using-the-method-override-header">
          Using the{' '}
          <code>
            <b>X-HTTP-Method-Override</b>
          </code>{' '}
          header
        </Header>
        <Paragraph size="2">
          Many of our endpoints require you to use the HTTP PATCH method. But some programming
          languages don't support PATCH. To work around this limitation, you can use the POST method
          instead of PATCH by including the{' '}
          <code>
            <b>X-HTTP-Method-Override</b>
          </code>{' '}
          header with your request, like this:
        </Paragraph>

        <CodeBlock headers={['X-HTTP-Method-Override: PATCH']} />

        <Header size="3" id="using-the-if-modified-header">
          Using the{' '}
          <code>
            <b>If-Modified-Since</b>
          </code>{' '}
          header
        </Header>
        <Paragraph size="2">
          The{' '}
          <code>
            <b>If-Modified-Since</b>
          </code>{' '}
          header limits the scope of the API response to only those of the requested resources that
          have been modified since a particular time. The header looks like this:
        </Paragraph>

        <CodeBlock headers={['If-Modified-Since: {ddd}, {D} {MMM} {YYYY} {HH}:{mm}:{ss} {Z}']} />

        <StyledNotice>
          <strong>NOTE:</strong> If your formatting codes are rusty,{' '}
          <code>Tue, 20 Jun 2023 14:42:36 GMT</code> is an example.
        </StyledNotice>

        <Paragraph size="2">
          The API returns a complete representation for each of the resources that meets these
          criteria. If there aren't any, you get back an empty response body and the HTTP status 304
          Not Modified. If you specify a time that occurs in the future, we ignore it and return the
          usual response.
        </Paragraph>

        <Paragraph size="2">
          This header works only with the endpoints in{' '}
          <Link variant="minimal" href="#table-3">
            Table 3
          </Link>
          . If you'd like us to add support for other endpoints,{' '}
          <Link variant="minimal" href="http://www.vimeo.com/help/contact">
            let us know
          </Link>
          .
        </Paragraph>

        <GuideTable
          tableNumber={3}
          description={
            <span>
              Endpoints that support the{' '}
              <code>
                <b>If-Modified-Since</b>
              </code>{' '}
              header
            </span>
          }
          headers={[]}
          rows={[
            [<code>{`/channels/{channel_id}/videos`}</code>],
            [<code>{`/groups/{group_id}/videos`}</code>],
            [
              <div>
                <code>{`/users/{user_id}/channels`}</code>
                <br />
                <code>{`/me/channels`}</code>
              </div>,
            ],
            [
              <div>
                <code>{`/users/{user_id}/videos`}</code>
                <br />
                <code>{`/me/videos`}</code>
              </div>,
            ],
            [
              <div>
                <code>{`/users/{user_id}/watchlater`}</code>
                <br />
                <code>{`/me/watchlater`}</code>
              </div>,
            ],
          ]}
        />

        <Header size="3" id="using-the-user-agent-header">
          Using the{' '}
          <code>
            <b>User-Agent</b>
          </code>{' '}
          header
        </Header>
        <Paragraph size="2">
          We recommend that you supply a custom user agent for your app through the{' '}
          <code>
            <b>User-Agent</b>
          </code>{' '}
          header, like this:
        </Paragraph>

        <CodeBlock headers={['User-Agent: {user_agent_string}']} />

        <Paragraph size="2">
          You don't need to follow a specific format on our account. Just make sure that the header
          identifies your app. Something like <code>your_app_name/1.0.2</code> is more than fine. If
          we see a generic{' '}
          <code>
            <b>User-Agent</b>
          </code>{' '}
          header, we might block your app's access to the API.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> If you're using our{' '}
          <Link variant="minimal" href="/api/libraries">
            SDKs
          </Link>
          , the recommended{' '}
          <code>
            <b>User-Agent</b>
          </code>{' '}
          header is already built in. You don't need to change it.
        </StyledNotice>

        <Header size="2" id="working-with-parameters">
          Working with parameters
        </Header>
        <Paragraph size="2">
          In many cases, you can attach parameters and their values to the end of an endpoint using
          query string notation. The API takes these parameters into account and modifies your
          request accordingly.
        </Paragraph>

        <Paragraph size="2">In this section, we discuss the following parameters:</Paragraph>

        <ul>
          <li>
            <Link variant="minimal" href="#json-filter">
              <code>
                <b>fields</b>
              </code>
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#using-the-sort-parameters">
              <code>
                <b>sort</b>
              </code>{' '}
              and{' '}
              <code>
                <b>direction</b>
              </code>
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#using-the-filter-parameter">
              <code>
                <b>filter</b>
              </code>
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#using-the-pagination-parameter">
              <code>
                <b>page</b>
              </code>{' '}
              and{' '}
              <code>
                <b>per_page</b>
              </code>
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#using-the-sizes-parameter">
              <code>
                <b>sizes</b>
              </code>
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#using-the-no-padding-parameter">
              <code>
                <b>no_padding</b>
              </code>
            </Link>
          </li>

          <Header size="3" id="json-filter">
            Using the{' '}
            <code>
              <b>fields</b>
            </code>{' '}
            parameter
          </Header>
          <Paragraph size="2">
            The{' '}
            <code>
              <b>fields</b>
            </code>{' '}
            parameter enables you to specify exactly which fields of a representation to return. If
            the field doesn't appear among your choices, the API doesn't return it, which can speed
            up response time dramatically.
          </Paragraph>

          <Paragraph size="2">Specify the desired fields as a comma-separated list:</Paragraph>

          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/me',
              params: '?fields=uri,name,metadata.connections.albums',
            }}
          />

          <Paragraph size="2">
            In this example, the API returns the{' '}
            <code>
              <b>uri</b>
            </code>
            ,{' '}
            <code>
              <b>name</b>
            </code>
            , and{' '}
            <code>
              <b>metadata.connections.albums</b>
            </code>{' '}
            fields of the user representation, which is otherwise pretty extensive. Notice that you
            can indicate nested fields with dot notation. You get each level of the hierarchy in the
            API response but none of the data at the higher levels. If there are additional nested
            fields below the field that you requested, which is the case with{' '}
            <code>
              <b>metadata.connections.albums</b>
            </code>
            , you get them all, including their data, even though you didn't explicitly request
            them. You just implicitly did. (Now we're getting philosophical.)
          </Paragraph>

          <CodeBlock language="json">
            {{
              uri: '/users/152184',
              name: 'Vimeo Staff',
              metadata: {
                connections: {
                  albums: {
                    uri: '/users/152184/albums',
                    options: ['GET'],
                    total: 1,
                  },
                },
              },
            }}
          </CodeBlock>

          <StyledNotice>
            <strong>NOTE:</strong> This example utilizes the GET method, but we support the{' '}
            <code>
              <b>fields</b>
            </code>{' '}
            parameter in every kind of call except for DELETE. In all cases, though, always send{' '}
            <code>
              <b>fields</b>
            </code>{' '}
            as a query parameter, never in the body of the request.
          </StyledNotice>

          <Header size="3" id="using-the-sort-parameters">
            Using the{' '}
            <code>
              <b>sort</b>
            </code>{' '}
            and{' '}
            <code>
              <b>direction</b>
            </code>{' '}
            parameters
          </Header>
          <Paragraph size="2">
            Certain endpoints support the{' '}
            <code>
              <b>sort</b>
            </code>{' '}
            and{' '}
            <code>
              <b>direction</b>
            </code>{' '}
            parameters, which enable you to sort the representations in a collection in any number
            of useful ways. (The exact sort methods depend on the endpoint, but{' '}
            <code>alphabetical</code> and <code>date</code> are common choices; see the{' '}
            <Link variant="minimal" href="/api/reference">
              API Reference
            </Link>{' '}
            for a complete list for your particular endpoint.) You indicate the sort method with the{' '}
            <code>
              <b>sort</b>
            </code>{' '}
            parameter, and then you specify{' '}
            <code>
              <b>direction</b>
            </code>{' '}
            as either <code>asc</code> or <code>desc</code> for ascending or descending,
            respectively.
          </Paragraph>

          <StyledNotice>
            <strong>NOTE:</strong> If you don't specify a direction, we default to <code>desc</code>
            .
          </StyledNotice>

          <Paragraph size="2">
            The following example returns the current user's videos sorted by date from oldest to
            newest:
          </Paragraph>

          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/me/videos',
              params: '?sort=date&direction=asc',
            }}
          />

          <Paragraph size="2">
            Not every endpoint is sortable like this. For those that are, we list{' '}
            <code>
              <b>sort</b>
            </code>{' '}
            and{' '}
            <code>
              <b>direction</b>
            </code>{' '}
            among their parameters in the{' '}
            <Link variant="minimal" href="/api/reference">
              API Reference
            </Link>
            . Please also note that, on certain endpoints, the{' '}
            <code>
              <b>sort</b>
            </code>{' '}
            parameter has a slightly different function. When it does, it appears by itself in the
            API Reference, without the{' '}
            <code>
              <b>direction</b>
            </code>{' '}
            parameter.
          </Paragraph>

          <Header size="3" id="using-the-filter-parameter">
            Using the{' '}
            <code>
              <b>filter</b>
            </code>{' '}
            parameter
          </Header>
          <Paragraph size="2">
            The{' '}
            <code>
              <b>filter</b>
            </code>{' '}
            parameter enables you to return results based on the attribute of a resource. The
            attribute in question depends on the endpoint. For example, because we live in a world
            where a channel resource can have the <code>featured</code> attribute, this request
            returns only those channels that do:
          </Paragraph>

          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/channels',
              params: '?filter=featured',
            }}
          />

          <Paragraph size="2">
            Many endpoints support this parameter, but many others don't, so you'll want to check
            the{' '}
            <Link variant="minimal" href="/api/reference">
              API Reference
            </Link>{' '}
            for the specifics. If{' '}
            <code>
              <b>filter</b>
            </code>{' '}
            is an option for an endpoint, we list it among its parameters.
          </Paragraph>

          <Header size="4" id="filtering-videos-by-content-rating">
            Filtering videos by content rating
          </Header>
          <Paragraph size="2">
            With a special use of the{' '}
            <code>
              <b>filter</b>
            </code>{' '}
            parameter, you can limit the results of any endpoint that returns video representations
            according to the content rating of the videos.
          </Paragraph>

          <Paragraph size="2">
            The syntax of the filter is <code>?filter=content_rating&filter_content_rating=x</code>,
            where{' '}
            <code>
              <b>x</b>
            </code>{' '}
            is the comma-separated list of the content ratings to get. See{' '}
            <Link variant="minimal" href="#table-4">
              Table 4
            </Link>{' '}
            for the content ratings that you can use.
          </Paragraph>

          <StyledNotice>
            <strong>NOTE:</strong> For more information about the content ratings system, see our{' '}
            <Link
              variant="minimal"
              href="https://vimeo.com/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426157083537-Content-ratings"
            >
              Help Center
            </Link>
            .
          </StyledNotice>

          <GuideTable
            tableNumber={4}
            description="Content ratings"
            headers={[]}
            rows={[
              [<code>language</code>],
              [<code>drugs</code>],
              [<code>violence</code>],
              [<code>nudity</code>],
              [<code>safe</code>],
              [<code>unrated</code>],
            ]}
          />

          <Paragraph size="2">
            The following request gets all Staff Pick videos that have the content ratings{' '}
            <code>safe</code> and <code>unrated</code>:
          </Paragraph>

          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/channels/staffpicks/videos',
              params: '?filter=content_rating&filter_content_rating=safe,unrated',
            }}
          />

          <Header size="3" id="using-the-pagination-parameter">
            Using the{' '}
            <code>
              <b>page</b>
            </code>{' '}
            and{' '}
            <code>
              <b>per_page</b>
            </code>{' '}
            parameters
          </Header>
          <Paragraph size="2">
            As we discussed in{' '}
            <Link variant="minimal" href="#working-with-representations">
              Working with representations
            </Link>{' '}
            above, the{' '}
            <code>
              <b>page</b>
            </code>{' '}
            and{' '}
            <code>
              <b>per_page</b>
            </code>{' '}
            parameters apply to collection representations that break across multiple pages. We
            advised you to stick with the URIs that you get in the fields of the collection
            representation, but you can also form your own URIs for special cases.
          </Paragraph>

          <Paragraph size="2">
            When none of the existing pagination fields provide a link to the exact page that you
            want, use{' '}
            <code>
              <b>page</b>
            </code>{' '}
            to return a particular page of the results:
          </Paragraph>

          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/videos/174560759/pictures',
              params: '?page=3',
            }}
          />

          <StyledNotice>
            <strong>NOTE:</strong> If you specify a page that doesn't exist, the API returns a 404
            error.
          </StyledNotice>

          <Paragraph size="2">
            Use{' '}
            <code>
              <b>per_page</b>
            </code>{' '}
            to set the number of representations per page from the default value of <code>25</code>{' '}
            up to a maximum value of <code>100</code>:
          </Paragraph>

          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/videos/174560759/pictures',
              params: '?per_page=50',
            }}
          />

          <Paragraph size="2">
            You can also use these parameters together. This example sets the number of
            representations per page to 1 and then returns the tenth page:
          </Paragraph>

          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/videos/174560759/pictures',
              params: '?per_page=1&page=10',
            }}
          />

          <Header size="3" id="using-the-sizes-parameter">
            Using the{' '}
            <code>
              <b>sizes</b>
            </code>{' '}
            parameter
          </Header>
          <Paragraph size="2">
            Images that you upload to Vimeo, such as video thumbnails and profile portraits, are
            converted into multiple files with different dimensions for different purposes.
            Normally, when the API returns image data, you get information about all the associated
            image sizes, which might be more than you want or need. The{' '}
            <code>
              <b>sizes</b>
            </code>{' '}
            parameter enables you to specify exactly which image data to retrieve according to the
            dimensions that you're looking for.
          </Paragraph>

          <Paragraph size="2">
            The syntax is <code>?sizes=x</code>, where{' '}
            <code>
              <b>x</b>
            </code>{' '}
            is the comma-separated list of dimensions that you want to return. Give each set of
            dimensions as <code>{`{width}x{height}`}</code>, so <code>295x166</code> for a video
            thumbnail of 295 ✕ 166 pixels or <code>100x100</code> for a profile portrait of 100 ✕
            100 pixels. To return both of these from a video resource:
          </Paragraph>

          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/videos/174560759',
              params: '?sizes=640x480,100x100',
            }}
          />

          <Paragraph size="2">
            If you don't know the corresponding height value, or if there isn't a corresponding
            height value, you can specify the width alone, either as <code>{`{width}x`}</code> or
            just <code>{`{width}`}</code>:
          </Paragraph>

          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/videos/174560759',
              params: '?sizes=640x,30',
            }}
          />

          <StyledNotice>
            <strong>NOTE:</strong> You can't specify dimensions by height alone; <code>x150</code>{' '}
            doesn't work.
          </StyledNotice>

          <Paragraph size="2">
            The only thing you can't do is return one set of dimensions for one type of image and
            another set of dimensions for another type of image. The API returns all images in the
            resource according to the same specified dimensions.
          </Paragraph>

          <Paragraph size="2">
            <Link variant="minimal" href="#table-5">
              Table 5
            </Link>{' '}
            lists the default image sizes for various resources.
          </Paragraph>

          <GuideTable
            tableNumber={5}
            description="Default image sizes by resource"
            headers={['Resource', 'Default sizes, pixels*']}
            rows={[
              [
                'Video thumbnail',
                <div>
                  100 ✕ 75
                  <br />
                  200 ✕ 150
                  <br />
                  295 ✕ 166
                  <br />
                  640
                  <br />
                  960
                  <br />
                  1280
                  <br />
                  1920
                  <br />
                </div>,
              ],
              ['Showcase logo', 200],
              ['Channel header', '980 ✕ 250'],
              ['Group header', '980 ✕ 250'],
              [
                'User portrait',
                <div>
                  30 ✕ 30
                  <br />
                  72 ✕ 72
                  <br />
                  75 ✕ 75
                  <br />
                  100 ✕ 100
                  <br />
                  216 ✕ 216
                  <br />
                  288 ✕ 288
                  <br />
                  300 ✕ 300
                  <br />
                  360 ✕ 360
                  <br />
                </div>,
              ],
            ]}
          />

          <Paragraph size="3">
            * If the size doesn't include a height value, we calculate the height automatically to
            maintain the aspect ratio of the original image. For example, if the original image is
            2560 ✕ 1920 pixels, the scaled image at size 640 is 640 ✕ 480 pixels.
          </Paragraph>

          <Header size="3" id="using-the-no-padding-parameter">
            Using the{' '}
            <code>
              <b>no_padding</b>
            </code>{' '}
            parameter
          </Header>
          <Paragraph size="2">
            The{' '}
            <code>
              <b>no_padding</b>
            </code>{' '}
            parameter enables you to remove the padding that appears around any image in our API
            responses. It can be used in conjunction with other image parameters, like{' '}
            <Link variant="minimal" href="#using-the-sizes-parameter">
              <code>
                <b>sizes</b>
              </code>
            </Link>
            , and is completely optional.
          </Paragraph>
          <Paragraph size="2">
            The way this works is, when you make a GET request for an image on Vimeo, the API sends
            back the URL of the image file with the query parameter{' '}
            <code>
              <b>r</b>
            </code>{' '}
            set to the value <code>pad</code> attached, as in <code>https://image-url?r=pad</code>.
            This is what adds padding to the image. To prevent <code>?r=pad</code> from appearing in
            the URL and adding the padding as described, include{' '}
            <code>
              <b>no_padding</b>
            </code>{' '}
            set to <code>true</code> in the request, like this:
          </Paragraph>
          <CodeBlock
            endpoint={{
              method: 'get',
              resource: '/videos/779937936',
              params: '?fields=pictures&no_padding=true&sizes=2048x1080',
            }}
          />

          <Header size="2" id="working-with-batch-requests">
            Working with batch requests
          </Header>
          <Paragraph size="2">
            Depending on the endpoint, you might be able to make a <em>batch request</em>, which
            bundles multiple actions into a single API call.
          </Paragraph>

          <Paragraph size="2">
            In the body of the batch request, add a JSON array for the fields and values that you
            want to pass to the endpoint. For example, the following request adds multiple
            categories to a video:
          </Paragraph>

          <CodeBlock
            language="json"
            endpoint={{ method: 'put', resource: '/videos/174560759/categories' }}
          >
            {[{ category: 'animation' }, { category: 'talks' }]}
          </CodeBlock>

          <Paragraph size="2">
            Batch requests are supported only on certain endpoints, so check the{' '}
            <Link variant="minimal" href="/api/reference">
              API Reference
            </Link>{' '}
            for details.
          </Paragraph>

          <Header size="2" id="whats-next">
            What's next?
          </Header>
          <Paragraph size="2">
            Now that you've seen a little of what the Vimeo API can do, it's time to explore some
            endpoints and start making some requests. The{' '}
            <Link variant="minimal" href="/api/reference">
              API Reference
            </Link>{' '}
            awaits you.
          </Paragraph>

          <Paragraph size="2">
            And just in case we've lost you, or if you have comments or questions about anything in
            this guide,{' '}
            <Link variant="minimal" href="http://vimeo.com/help/contact">
              contact us
            </Link>{' '}
            and let us know.
          </Paragraph>
        </ul>
      </GuideWrapper>
    </>
  );
}
